import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { Grid2 as Grid, Paper, TextField, Box, Button, Link, IconButton, Tooltip } from '@mui/material';
import { Refresh as RefreshIcon, Download as DownloadIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@contexts/AuthContext';
import { useSnackbar } from '@contexts/SnackbarContext';
import { extractBestTranslation } from '@/utils/helpers';
import SendoutIsTestChip from '@components/sendout/SendoutIsTestChip';
import SendoutStatusChip from '@components/sendout/SendoutStatusChip';
import { DateTime } from 'luxon';
import { formatDateTime } from '@/utils/helpers';
import API from '@/utils/axios';

const SendoutEditionForm = ({ formData, onSubmit, handleRefresh, fieldsVariant = "standard" }) => {
    const { t, i18n } = useTranslation();
    const [workflowUrl, setWorkflowUrl] = useState(null);
    const { isSuperAdmin } = useAuth();
    const { showMessage } = useSnackbar();

    const { control, handleSubmit, setValue } = useForm();

    useEffect(() => {
        if (formData) {
            setValue("description", formData.description || "");
            setValue("planned_at", formData.planned_at ? DateTime.fromISO(formData.planned_at) : null);
            setWorkflowUrl(formData.workflow_customer_id === null
                ? `/workflows/templates/${formData.workflow_id}`
                : `/workflows/custom/${formData.workflow_id}`);
        }
    }, [formData, setValue]);

    const handleDownloadExport = async () => {
        try {
            const response = await API.get(`/sendouts/${formData.id}/export?lang=${i18n.language}`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            
            // Extraire le nom de fichier depuis l'en-tête Content-Disposition
            console.log(response.headers);
            const contentDisposition = response.headers['content-disposition'];
            let filename = `sendout_${formData.id}_progress.csv`; // Nom par défaut
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            
            // Nettoyer
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);
            showMessage(t('An error occurred while downloading the export.'), 'error');
        }
    };

    return (
        <Paper sx={{ p: 2 }}>
            <form onSubmit={handleSubmit(onSubmit)}>

                <Grid container>
                    <Grid size={{ xs: 12, md: 6 }}>

                        <Box sx={{ mb: 1 }}>
                            <SendoutIsTestChip is_test={formData.is_test} />
                            {' '}
                            <SendoutStatusChip status={formData.status} missing_credits_type={formData.missing_credits_type} planned_at={formData.planned_at} />
                            <Tooltip title={t('Refresh')} placement="top">
                                <IconButton sx={{ mt: 1 }} aria-label={t('Refresh')} color="primary"
                                    style={{ marginTop: 0 }}
                                    onClick={handleRefresh}>
                                    <RefreshIcon sx={{ cursor: 'pointer' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {isSuperAdmin() && (
                            <Box>
                                {t('Customer')} : {' '}
                                {formData.customer_name}
                            </Box>
                        )}

                        <Box>
                            {t('Workflow')} : {' '}
                            <Link to={workflowUrl} component={RouterLink} color="inherit" onClick={(event) => event.stopPropagation()}>
                                {extractBestTranslation(formData.workflow_name, i18n.language)}
                            </Link>
                        </Box>

                        <Box>
                            {t('Mailing list')} : {' '}
                            <Link to={`/mailinglists/${formData.mailing_list_id}`} component={RouterLink} color="inherit" onClick={(event) => event.stopPropagation()}>
                                {formData.mailing_list_name}
                            </Link>
                        </Box>

                        <Box>
                            {t('Created by {{user}} on {{date}}', { 
                                user: formData.created_by 
                                    ? formData.created_by.first_name + ' ' + formData.created_by.last_name
                                    : t('Unknown user'), 
                                date: formatDateTime(formData.created_at) })}
                        </Box>

                        <Box>
                            <Link 
                                component="button"
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDownloadExport();
                                }}
                                sx={{ cursor: 'pointer' }}
                            >
                                <DownloadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                {t('Download progress export')}
                            </Link>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }} sx={{ mt: { xs: 2, md: 0 } }}>

                        {formData.status === 'Planned' && (
                            <Box sx={{ mb: 2 }}>
                                <Controller
                                    name="planned_at"
                                    control={control}
                                    render={({ field }) => (
                                        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
                                        <DateTimePicker
                                            {...field}
                                            ampm={false}
                                            label={t('Planned date')}
                                            slotProps={{
                                                textField: {
                                                    variant: fieldsVariant,
                                                    sx: { width: 180 }
                                                }
                                            }}
                                            />
                                        </LocalizationProvider>
                                    )}
                                />
                            </Box>
                        )}

                        <Controller
                            name="description"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    multiline
                                    label={t('Description')}
                                    variant="outlined"
                                />
                            )}
                        />
                        <Box sx={{ mt: 1 }}>
                            <Button type="submit" variant="contained" color="primary" size="small">
                                {t('Save')}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

            </form>
        </Paper >
    );
};

export default SendoutEditionForm;
